/**
 * The cloth filter that makes the mark wave.
 *
 * `feTurbulence` generates a noise field and `feDisplacementMap` pushes the
 * logo's pixels around by it. Animating the turbulence — both its frequency
 * and, more importantly, a long horizontal drift — makes the distortion
 * travel across the mark instead of shimmering in place, which is the
 * difference between cloth in wind and a heat haze.
 *
 * It lives in its own component because more than one surface wants it and
 * the filter has an id. Two copies in one document would be two elements
 * claiming `#sx-flag`, and which one won would depend on render order — the
 * kind of bug that appears only once both surfaces are on screen together.
 *
 * Render it once per page. `FlagWave` below does that for the common case.
 */
export function FlagFilter() {
  return (
    <svg className="sx-flag-defs" aria-hidden="true" focusable="false">
      <defs>
        <filter id="sx-flag" x="-25%" y="-25%" width="150%" height="150%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.008 0.022"
            numOctaves="2"
            seed="7"
            result="noise"
          >
            <animate
              attributeName="baseFrequency"
              dur="4.5s"
              values="0.012 0.05; 0.018 0.038; 0.012 0.05"
              repeatCount="indefinite"
            />
          </feTurbulence>
          {/* The drift. Without it the noise stays put and the mark shimmers
              rather than waves. */}
          <feOffset in="noise" dx="0" dy="0" result="drift">
            <animate attributeName="dx" dur="3.4s" values="0; 18; 0" repeatCount="indefinite" />
          </feOffset>
          {/* Pinned at the left like cloth at a mast, loosest on the right.
              Applied flat, the wordmark smeared and stopped reading. */}
          <feDisplacementMap
            in="SourceGraphic"
            in2="drift"
            scale="3.2"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}

interface FlagWaveProps {
  children: React.ReactNode;
  /**
   * Render the filter definition alongside. Leave it off where the page
   * already has one — the login hero defines its own.
   */
  withFilter?: boolean;
  className?: string;
}

/**
 * Wraps something in the waving effect.
 *
 * The wrapper carries padding and a negative margin of the same size: the
 * displacement pushes pixels outside the element box, and with no room to
 * move the edges of the mark get clipped flat.
 */
export function FlagWave({ children, withFilter = true, className }: FlagWaveProps) {
  return (
    <>
      {withFilter ? <FlagFilter /> : null}
      <span className={`sx-flag${className ? ` ${className}` : ''}`}>{children}</span>
    </>
  );
}
